import { isEmptyOrSpaces } from './DataValidity.Helper';

/** Provides a new guid string */
export function GetNewGuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Gets JWT Token Expiry Time in Minutes. This is required in order to ensure the token is
 * refreshed silently without asking the user to make the change.
 */
export const GetJwtTokenExpiryTimeInMinutes = (token: string) => {
  if (isEmptyOrSpaces(token)) {
    return -60;
  }
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const payload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
  const jsonPayload = JSON.parse(payload);
  const currentTime = new Date();
  const expireInMinutes = (currentTime.getTime() - jsonPayload['exp'] * 1000) / 60000;
  return expireInMinutes;
};

export const shiftDateByMinutes = (minutesShift: number): Date => {
  const now = new Date();
  if (minutesShift) {
    const shiftedDate = new Date(now.getTime() + minutesShift * 60000);
    return shiftedDate;
  }
  return now;
};

export const getTimeInBrowserTimezone = (dateTimeUTC: string) => {
  const parsedDateUTC = new Date(dateTimeUTC);
  return convertDateToBrowserTimezone(parsedDateUTC);
};

//converts date from utc to browser timezone date
export const convertDateToBrowserTimezone = (dateTimeUTC: Date) => {
  // offset is given as: offset = UTCTime - LocalTime
  // thus LocalTime = UTCTime - offset
  const timezoneOffsetInMinutes = dateTimeUTC.getTimezoneOffset();
  return new Date(dateTimeUTC.getTime() - timezoneOffsetInMinutes * 60000);
};

export const displayDate = (dateTime: string, locale: string) => {
  const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  return getTimeInBrowserTimezone(dateTime).toLocaleDateString(locale, options);
};

export const getDateTimeInternationalFormat = (dateTime: Date, locale: string): string => {
  const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
  return dateTime.toLocaleTimeString(locale, options);
};

export const displayTime = (date: string | undefined, enableLocalTimeZone: boolean, locale: string) => {
  if (date) {
    return getFullLocalTime(new Date(date), locale, enableLocalTimeZone);
  }

  return '';
};

export const getFullLocalTime = (date: Date, locale: string, enableLocalTimeZone: boolean) => {
  if (enableLocalTimeZone) {
    // Covert To Local Time Zone and format of String is also determined by Locale
    return date.toLocaleString(locale, { timeZoneName: 'short' });
  }
  // Time Zone is UTC but the string format is determined by Locale.
  return date.toLocaleString(locale, { timeZoneName: 'short', timeZone: 'UTC' });
};

//converts from stringified date object in utc to local time in 12hr readable format
export const convertDateTimeTolocaleShortTime = (
  dateTimeUTC: string,
  locale: string,
  removeLeadingZero: boolean = true
) => {
  const localDateTime = getTimeInBrowserTimezone(dateTimeUTC);
  let timeString = localDateTime.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: true });

  if (removeLeadingZero && timeString.length > 0 && timeString.charAt(0) === '0') {
    timeString = timeString.substring(1);
  }

  return timeString;
};

function toTitleCase(phrase: string) {
  return phrase
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function truncateContent(value: string, threshold: number, addDots: boolean = false): [string, boolean] {
  if (value) {
    const shouldBeTrimmed = value.length > threshold;

    let modifiedValue = shouldBeTrimmed ? value.substring(0, value.substring(0, threshold).lastIndexOf(' ')) : value;
    if (shouldBeTrimmed && addDots) {
      modifiedValue = modifiedValue + '...';
    }
    return [modifiedValue, shouldBeTrimmed];
  }
  return [value, false];
}

export const getTimeZoneNameLocalized = (locale: string): string => {
  const timeZone = new Date().toLocaleDateString(locale, { timeZoneName: 'long', weekday: 'long' });
  const timeZoneText = timeZone.substr(timeZone.indexOf(' ') + 1);
  return '(' + toTitleCase(timeZoneText) + ')';
};

export const formatFileSize = (size: number) => {
  if (size < 1024) {
    return size + ' bytes';
  } else if (size < 1024 * 1024) {
    return (size / 1024.0).toFixed(0) + ' KB';
  } else if (size < 1024 * 1024 * 1024) {
    return (size / 1024.0 / 1024.0).toFixed(1) + ' MB';
  } else {
    return (size / 1024.0 / 1024.0 / 1024.0).toFixed(1) + ' GB';
  }
};

export const trapFocus = (
  event: KeyboardEvent,
  firstItemKey: string,
  lastItemKey: string,
  altLastItemKey: string | null
) => {
  const keyCode = event.key;
  if (keyCode === 'Tab') {
    if (event.shiftKey) {
      const firstItem = document.getElementById(firstItemKey);
      const lastItem = document.getElementById(lastItemKey);
      const altLastItem = altLastItemKey ? document.getElementById(altLastItemKey) : undefined;

      if (firstItem && document.activeElement === firstItem) {
        if ((lastItem as any)?.disabled) {
          altLastItem?.focus();
        } else {
          lastItem?.focus();
        }

        event.preventDefault();
      } else {
        if ((lastItem as any)?.disabled) {
          if (altLastItem && document.activeElement === altLastItem) {
            firstItem?.focus();
            event.preventDefault();
          }
        } else if (lastItem && document.activeElement === lastItem) {
          lastItem?.focus();
          event.preventDefault();
        }
      }
    }
  }
};

export const getCookieValue = (name: string) => {
  return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
};

//TODO: remove when updating to enablers 6.x
export const openLinkInNewTab = (link: string) => {
  return window.open(link, '_blank');
};

//Chrome and Firefox 32+, Navigator.languages contains an array of locales in order of user preference, and is more accurate than navigator.language. For backwards compatibility we will default to navigator.language
export const getLanguageFromBrowser = () => {
  if (navigator.languages !== undefined) return navigator.languages[0];
  return navigator.language;
};
